import type {
  BuildingOrListing,
  ExtendedListing,
  FetchByCoordsParams,
  FetchByCoordsResponse,
} from '@/src/lib/api/endpoints/listing';
import type { InfoCardListing, MarkerIconType } from '@/src/components/features/search/Map/types';
import { generateMarkerId } from './generateMarkerId';
import { getListingIconType } from './listingPropertyType';
import { getListingHdpHref } from './listingHref';

type MapMarkerInput = {
  markerId: string;
  northSouthLat: number;
  eastWestLng: number;
  statusType: 'FOR_RENT';
  listingData: InfoCardListing;
  iconType: MarkerIconType;
  userItemTypes?: Array<string>;
};

type ListingParamsInput = {
  mapBounds: { northLat: number; southLat: number; eastLng: number; westLng: number } | null;
  area: { minLat?: number; maxLat?: number; minLon?: number; maxLon?: number } | null | undefined;
  offset: number;
  showBorder: boolean;
  effectiveArea: { id?: string } | null | undefined;
  limit: number;
};

/**
 * Caps the photo arrays on a listing-shaped object so we don't ship dozens of
 * URLs per card across the RSC / React Query boundary. Applied both to raw
 * buildings before SSR serialization and to normalized listings — byCoordsV2
 * can return 50+ URLs per listing when includePhotosCollection=true.
 */
export const LISTING_PHOTO_CAP = 12;

export function capListingPhotos<T extends { medPhotoUrls?: Array<string> }>(listing: T): T {
  if (!listing.medPhotoUrls || listing.medPhotoUrls.length <= LISTING_PHOTO_CAP) return listing;
  return { ...listing, medPhotoUrls: listing.medPhotoUrls.slice(0, LISTING_PHOTO_CAP) };
}

/**
 * Caps photo arrays on every listing and nested listing in a raw API response.
 * Call on the server before passing the response across the RSC boundary to
 * shrink serialized payload.
 */
export function capResponsePhotos(response: FetchByCoordsResponse): FetchByCoordsResponse {
  if (!response.buildings) return response;
  return {
    ...response,
    buildings: response.buildings.map((building) => {
      const capped = capListingPhotos(building);
      if (capped.listings) {
        return { ...capped, listings: capped.listings.map(capListingPhotos) };
      }
      return capped;
    }),
  };
}

/**
 * Flattens nested building/listing API response into a deduplicated array of listings.
 *
 * Handles two API response structures:
 * 1. Flat listings (trimResponse: true) - buildings array contains listing objects directly
 * 2. Nested buildings (trimResponse: false) - buildings contain nested listings arrays
 *
 * Copies building-level geo/lotIdEncoded/neighborhoods to nested listings and
 * deduplicates by generateMarkerId.
 */
export function normalizeListings(buildings: Array<BuildingOrListing>): Array<ExtendedListing> {
  const allListings: Array<ExtendedListing> = buildings.flatMap((building) => {
    if (building.listings && Array.isArray(building.listings)) {
      return building.listings.map(
        (listing): ExtendedListing => ({
          ...listing,
          geo: listing.geo || building.geo,
          lotIdEncoded: listing.lotIdEncoded || building.lotIdEncoded,
          neighborhoods: listing.neighborhoods || building.neighborhoods,
        }),
      );
    }
    return [building];
  });

  const deduped = Array.from(
    new Map(allListings.map((listing) => [generateMarkerId(listing), listing])).values(),
  );
  return deduped.map(capListingPhotos);
}

function extractInfoCardData(listing: ExtendedListing): InfoCardListing {
  const hdpUrl = getListingHdpHref(listing) ?? undefined;
  // Strip undefined values before this data is dehydrated into the React Query cache.
  return JSON.parse(JSON.stringify({ ...listing, hdpUrl })) as InfoCardListing;
}

const VALID_ICON_TYPES = new Set<MarkerIconType>(['building', 'condo', 'house', 'room']);

function deriveIconType(listing: ExtendedListing): MarkerIconType {
  // Prefer shared property type logic (handles v3 propertyType + listingType)
  const fromPropertyType = getListingIconType(listing.propertyType, listing.listingType);
  if (fromPropertyType !== 'building') return fromPropertyType;

  // Fall back to v2 iconType field for listings without a propertyType
  const v2IconType = (listing as Record<string, unknown>).iconType as string | undefined;
  if (v2IconType && VALID_ICON_TYPES.has(v2IconType as MarkerIconType)) return v2IconType as MarkerIconType;

  return 'building';
}

/**
 * Builds map marker data from a list of listings.
 * Filters out listings without geo coordinates.
 */
export function buildListingMarkers(listings: Array<ExtendedListing>): Array<MapMarkerInput> {
  return listings
    .filter((listing) => listing.geo?.lat && listing.geo?.lon)
    .map((listing) => ({
      markerId: generateMarkerId(listing),
      northSouthLat: listing.geo!.lat,
      eastWestLng: listing.geo!.lon,
      statusType: 'FOR_RENT' as const,
      listingData: extractInfoCardData(listing),
      iconType: deriveIconType(listing),
      userItemTypes: listing.userItemTypes,
    }));
}

/**
 * Builds listing fetch params from map bounds or area bounds.
 * Returns null when neither bounds source is available.
 */
export function buildListingParams(input: ListingParamsInput): FetchByCoordsParams | null {
  const { mapBounds, area, offset, showBorder, effectiveArea, limit } = input;

  if (mapBounds) {
    return {
      minLat: mapBounds.southLat,
      maxLat: mapBounds.northLat,
      minLon: mapBounds.westLng,
      maxLon: mapBounds.eastLng,
      limit,
      offset,
      ...(showBorder && effectiveArea?.id ? { areas: effectiveArea.id } : {}),
    };
  }
  if (area?.minLat && area?.maxLat && area?.minLon && area?.maxLon) {
    return {
      minLat: area.minLat,
      maxLat: area.maxLat,
      minLon: area.minLon,
      maxLon: area.maxLon,
      limit,
      offset,
      ...(showBorder && effectiveArea?.id ? { areas: effectiveArea.id } : {}),
    };
  }
  return null;
}
